import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@material-ui/core";
import { Environment, Service } from "../_generated";
import styles from "./InfoTable.module.css";
import { Update, CloudUploadOutlined } from "@material-ui/icons";
import moment from "moment";
import { BackendURL } from "../globals";
import { authToken } from "../components/Authentication";
import axios from "axios";
import EnvironmentUpdateDialog from "./EnvironmentUpdateDialog";
import analytics from "../analytics";
import EnvironmentMoveDialog from "./EnvironmentMoveDialog";

type ServiceExtended = Service & {
  lb_url?: string;
  dggr_domain?: string;
  custom_domain?: string;
};

export default function EnvironmentInfo({
  environment,
  projectName,
}: {
  environment: Environment;
  projectName: string;
}) {
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [services, setServices] = useState([] as ServiceExtended[]);

  const apiRoot = `${BackendURL}/api`;

  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  useEffect(() => {
    analytics.page("EnvironmentInfo");
  }, []);

  useEffect(() => {
    const url = `${apiRoot}/projects/${projectName}/environments/${environment.pk}/last_deployment/`;
    axios.get(url).then((response) => {
      const serviceHash = response.data.outputs.services || {};
      const services = Object.keys(serviceHash).map((name) => {
        const service = serviceHash[name] as ServiceExtended;
        return { ...service };
      });
      setServices(services);
    });
  }, [apiRoot, environment, projectName]);

  async function onEnvironmentUpdate() {
    await axios.post(
      `${apiRoot}/projects/${projectName}/environments/${environment.pk}/apply/`
    );
    setShowUpdateDialog(false);
    analytics.track("Environment Update Confirmed");
  }

  async function onEnvironmentMove() {
    await axios.post(
      `${apiRoot}/projects/${projectName}/environments/${environment.pk}/apply/`
    );
    setShowMoveDialog(false);
    analytics.track("Environment Update Confirmed");
  }

  return (
    <>
      <Grid container justify="center" spacing={2}>
        <Grid item>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">
                {environment.alias || environment.name}
              </Typography>
              <table className={styles.infoTable}>
                <tbody>
                  <tr>
                    <th>ID:</th>
                    <td>{environment.name}</td>
                  </tr>
                  <tr>
                    <th>Region:</th>
                    <td>{environment.region}</td>
                  </tr>
                  <tr>
                    <th>Target:</th>
                    <td>{environment.target}</td>
                  </tr>
                  <tr>
                    <th>Created:</th>
                    <td>
                      {environment.date_created
                        ? moment(environment.date_updated).toLocaleString()
                        : "never"}
                    </td>
                  </tr>
                  <tr>
                    <th>Last updated:</th>
                    <td>
                      {environment.date_updated
                        ? moment(environment.date_updated).toLocaleString()
                        : "never"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
            <CardActions>
              <Grid container justify="center">
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Update />}
                    onClick={() => setShowUpdateDialog(true)}
                  >
                    Update
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    style={{ marginLeft: "10px" }}
                    startIcon={<CloudUploadOutlined />}
                    onClick={() => setShowMoveDialog(true)}
                  >
                    Move
                  </Button>
                </Grid>
              </Grid>
            </CardActions>
          </Card>
        </Grid>
        <Grid item>
          <ServiceUrlList services={services} />
        </Grid>
      </Grid>

      <EnvironmentUpdateDialog
        open={showUpdateDialog}
        environment={environment}
        onCancel={() => setShowUpdateDialog(false)}
        onConfirm={() => onEnvironmentUpdate()}
      />

      <EnvironmentMoveDialog
        open={showMoveDialog}
        environment={environment}
        onCancel={() => setShowMoveDialog(false)}
        onConfirm={() => onEnvironmentMove()}
      />
    </>
  );
}

function ServiceUrlList(props: { services: ServiceExtended[] }) {
  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table className={styles.variablesTable}>
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Load balancer URL</TableCell>
              <TableCell>Public URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...props.services].map((service) => (
              <TableRow>
                <TableCell>{service.name}</TableCell>
                <TableCell>
                  <a href={"http://" + service.lb_url}>{service.lb_url}</a>
                </TableCell>
                <TableCell>
                  {service.custom_domain && (
                    <a href={"https://" + service.custom_domain}>
                      {service.custom_domain}
                    </a>
                  )}
                  {!service.custom_domain && service.dggr_domain && (
                    <a href={"https://" + service.dggr_domain}>
                      {service.dggr_domain}
                    </a>
                  )}
                  {!(service.custom_domain || service.dggr_domain) &&
                    "domain not configured"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
